import { cn } from '@neo/lib/utils'
import { domAnimation, LazyMotion, MotionConfig } from 'framer-motion'
import React, { useEffect } from 'react'
import { useEscapeKey } from 'v2/shared/hooks/use-escape-key'
import { useFlowStore } from '../../../store'
import { useVibeAgentHidden } from './use-vibe-agent-gate'
import '../style.scss'
import { VibeAgentChat } from './chat'
import { useResizablePanel } from './use-resizable-panel'

export const VibeAgentSidebar = React.memo(function VibeAgentSidebar() {
  const showVibeSidebar = useFlowStore((state) => state.showVibeSidebar)
  const setShowVibeSidebar = useFlowStore((state) => state.setShowVibeSidebar)
  const setVibeSidebarWidth = useFlowStore((state) => state.setVibeSidebarWidth)
  const { width, minWidth, maxWidth, isResizing, startResize, resizeBy, resizeTo } =
    useResizablePanel()
  const vibeAgentHidden = useVibeAgentHidden()

  useEffect(() => {
    setVibeSidebarWidth(width)
  }, [width, setVibeSidebarWidth])

  useEscapeKey(() => {
    setShowVibeSidebar(false)
  })

  if (vibeAgentHidden) return null

  return (
    <div
      data-testid="vibe-agent-sidebar"
      className={cn(
        // Strong ease-out: linear reads mechanical on a panel; and while the
        // user drag-resizes, the transition must be OFF or the panel lags the
        // cursor by 200ms on every pointermove.
        'relative flex-shrink-0 h-full bg-background text-foreground transition-[width,visibility] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] overflow-hidden',
        showVibeSidebar ? 'visible mr-3 mb-3 rounded-lg border border-border' : 'w-0 invisible',
        isResizing && 'select-none transition-none',
      )}
      style={showVibeSidebar ? { width: `${width}px` } : undefined}
    >
      <hr
        data-testid="separator-vibe-agent-resize"
        tabIndex={0}
        aria-orientation="vertical"
        aria-label="Resize Vibe Agent panel"
        aria-valuemin={minWidth}
        aria-valuemax={maxWidth}
        aria-valuenow={width}
        onPointerDown={startResize}
        onKeyDown={(event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault()
            resizeBy(16)
          } else if (event.key === 'ArrowRight') {
            event.preventDefault()
            resizeBy(-16)
          } else if (event.key === 'Home') {
            event.preventDefault()
            resizeTo(minWidth)
          } else if (event.key === 'End') {
            event.preventDefault()
            resizeTo(maxWidth)
          }
        }}
        className={cn(
          'absolute bottom-0 left-0 top-0 z-10 m-0 w-1.5 cursor-ew-resize border-0 p-0 transition-colors hover:bg-brand/15 focus-visible:bg-brand/15 focus-visible:outline-none',
          isResizing && 'bg-brand/15',
        )}
      />
      {/* Feature-scoped LazyMotion: vibe components use `m.` so framer's full
          runtime is not bundled for the whole page. */}
      <LazyMotion features={domAnimation} strict>
        {/* Respect prefers-reduced-motion: Motion drops transform movement,
            keeps comprehension-aiding opacity fades. */}
        <MotionConfig reducedMotion="user">
          {/* Pinned at the resting width so the open/close width tween REVEALS
              a rigid surface instead of rewrapping every text line per frame. */}
          <div style={{ width: `${width}px` }} className="h-full">
            <VibeAgentChat />
          </div>
        </MotionConfig>
      </LazyMotion>
    </div>
  )
})
